import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Separator } from '@/components/ui/separator';
import {
  flattenContextVariables,
  useVariableLabel,
} from '@/modules/tryIt/hooks/useContextVariables';
import { selectSelectedTurn } from '@/modules/tryIt/redux/selectors';
import { PanelSectionTitle } from '@/modules/tryIt/shared/InfoRow';
import { TagChips } from '@/modules/tryIt/shared/TagChips';

import { NoContextPanel } from './NoContextPanel';
import { ReadonlyVariableRow } from './ReadonlyVariableRow';

/** Read-only Context tab for replay — the turn's context variables and tags. */
export const ContextPanelReplay = () => {
  const { t } = useTranslation();
  const variableLabel = useVariableLabel();
  const turn = useSelector(selectSelectedTurn);

  // The analytics payload stores context variables top-level (e.g. `account_id`,
  // `campaign`), not under `global.bundle`. Flatten them the same way the
  // test-mode editor does so replay shows what was active that turn.
  const variables = useMemo(
    () =>
      flattenContextVariables(turn?.metadata.context).sort((a, b) =>
        variableLabel(a.label).localeCompare(variableLabel(b.label))
      ),
    [turn?.metadata.context, variableLabel]
  );
  const tags = turn?.metadata.tags ?? [];

  if (variables.length === 0 && tags.length === 0) {
    return <NoContextPanel />;
  }

  return (
    <div className="flex flex-1 flex-col items-start gap-6 self-stretch">
      {variables.length > 0 && (
        <div className="flex w-full flex-col gap-3">
          <PanelSectionTitle>{t('try_it.context.variables_label')}</PanelSectionTitle>
          <div className="-mx-3 flex flex-col">
            {variables.map(({ label, value }) => (
              <ReadonlyVariableRow key={label} name={variableLabel(label)} value={value} />
            ))}
          </div>
        </div>
      )}

      {variables.length > 0 && tags.length > 0 && <Separator />}

      {tags.length > 0 && (
        <div className="flex w-full flex-col gap-3">
          <PanelSectionTitle>{t('try_it.context.tags_label')}</PanelSectionTitle>
          <TagChips tags={tags} />
        </div>
      )}
    </div>
  );
};
